<script lang="ts" setup>
import { computed, nextTick, reactive, ref } from 'vue'

import SancareOcticon from './SancareOcticon.vue'

const props = withDefaults(
  defineProps<{
    label?: string
    currentPage: number
    nbDisplayedPages?: number // pages to be displayed before and after current page
    pageCount: number
    previousNextButtonsEnabled?: boolean
  }>(),
  {
    label: 'Page',
    nbDisplayedPages: 2,
    previousNextButtonsEnabled: false,
  },
)

const emit = defineEmits<{
  (e: 'set-page', page: number): void
}>()

const pageInputBefore = ref<HTMLInputElement | null>(null)
const pageInputAfter = ref<HTMLInputElement | null>(null)

const pageInput = reactive({
  before: {
    show: false,
    value: '',
  },
  after: {
    show: false,
    value: '',
  },
})

const pagesMiddle = computed(() => {
  const list: number[] = []

  for (let i = props.currentPage - props.nbDisplayedPages; i < props.currentPage; i++) {
    if (i > 1) {
      list.push(i)
    }
  }

  if (props.currentPage > 1 && props.currentPage < props.pageCount) {
    list.push(props.currentPage)
  }

  for (let i = props.currentPage + 1; i <= props.currentPage + props.nbDisplayedPages; i++) {
    if (i < props.pageCount) {
      list.push(i)
    }
  }

  return list
})

function setPage(pageNb: number) {
  if (pageNb < 1 || pageNb > props.pageCount || pageNb === props.currentPage) return
  emit('set-page', pageNb)
}

function showPageInput(position: 'before' | 'after') {
  pageInput[position].show = true
  nextTick(() => {
    (position === 'before' ? pageInputBefore.value : pageInputAfter.value)?.focus()
  })
}

function updatePageInput(position: 'before' | 'after') {
  let newPage = Number(pageInput[position].value)
  if (Number.isNaN(newPage)) newPage = props.currentPage

  newPage = Math.min(Math.max(newPage, 1), props.pageCount)
  setPage(newPage)

  pageInput[position].show = false
  pageInput[position].value = ''
}
</script>

<template>
  <div class="pagination row g-0 justify-content-center align-items-center">
    <div class="col-auto">
      <div
        v-if="label !== ''"
        class="page-title"
      >
        {{ label }}
      </div>
      <ul class="pagination row g-0 m-0 justify-content-center">
        <li
          v-if="previousNextButtonsEnabled"
          class="col-auto page-item"
          :class="{ disabled: currentPage === 1 }"
        >
          <button
            class="page-link"
            type="button"
            :disabled="currentPage === 1"
            @click="setPage(currentPage - 1)"
          >
            <sancare-octicon
              name="chevron-left"
              :width="15"
              :height="15"
            />
          </button>
        </li>
        <li
          class="col-auto page-item"
          :class="{ active: currentPage === 1 }"
        >
          <button
            class="page-link"
            type="button"
            @click="setPage(1)"
          >
            1
          </button>
        </li>
        <li
          v-if="currentPage > 2 + nbDisplayedPages"
          class="col-auto page-item"
        >
          <button
            v-if="!pageInput.before.show"
            class="page-link"
            type="button"
            @click="showPageInput('before')"
          >
            …
          </button>
          <input
            v-else
            ref="pageInputBefore"
            v-model="pageInput.before.value"
            class="input-page p-1 mx-1"
            type="number"
            @change="updatePageInput('before')"
          >
        </li>
        <li
          v-for="pageNB in pagesMiddle"
          :key="pageNB"
          class="col-auto page-item"
          :class="{ active: pageNB === currentPage }"
        >
          <button
            class="page-link"
            type="button"
            @click="setPage(pageNB)"
          >
            {{ pageNB }}
          </button>
        </li>
        <li
          v-if="currentPage < pageCount - 1 - nbDisplayedPages"
          class="col-auto page-item"
        >
          <button
            v-if="!pageInput.after.show"
            class="page-link"
            type="button"
            @click="showPageInput('after')"
          >
            …
          </button>
          <input
            v-else
            ref="pageInputAfter"
            v-model="pageInput.after.value"
            class="input-page p-1 mx-1"
            type="number"
            @change="updatePageInput('after')"
          >
        </li>
        <li
          v-if="pageCount > 1"
          class="col-auto page-item"
          :class="{ active: currentPage === pageCount }"
        >
          <button
            class="page-link"
            type="button"
            @click="setPage(pageCount)"
          >
            {{ pageCount }}
          </button>
        </li>
        <li
          v-if="previousNextButtonsEnabled"
          class="col-auto page-item"
          :class="{ disabled: currentPage === pageCount }"
        >
          <button
            class="page-link"
            type="button"
            :disabled="currentPage === pageCount"
            @click="setPage(currentPage + 1)"
          >
            <sancare-octicon
              name="chevron-right"
              :width="15"
              :height="15"
            />
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>

<style lang="scss" scoped>
input.input-page {
  width: 30px;
  padding: 0;
  margin: 0;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: textfield;
}

input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
}
</style>
